import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// ─── Constantes de layout ───────────────────────────────────────────
const LAYOUT = {
  NODE_W: 220,
  NODE_H: 52,
  ROOT_NODE_H: 60,
  ROOT_Y: 40,
  BRANCH_START_Y: 150,
  STEP_Y: 80,
  GROUP_GAP: 80,
  BRANCH_GAP: 30,
  PADDING: 40,
};

const DOMAIN_COLORS = {
  humain: '#3b82f6',
  ecologique: '#22c55e',
  culturel: '#f97316',
};

// ─── Helpers ────────────────────────────────────────────────────────
function normalizeRoot(description) {
  return description
    .toLowerCase()
    .trim()
    .replace(/[\s\u2014\u2013-]+/g, ' ')
    .replace(/[.,;:!?\u2026]+$/g, '')
    .replace(/\s+/g, ' ');
}

function truncate(text, max = 30) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '\u2026' : text;
}

function scoreBadgeColor(score) {
  if (score === null || score === undefined) return '#9ca3af';
  if (score > 0.5) return '#22c55e';
  if (score >= 0.2) return '#f59e0b';
  return '#3b82f6';
}

function domainColor(domain) {
  return DOMAIN_COLORS[domain] || '#6b7280';
}

function bezierPath(x1, y1, x2, y2) {
  const midY = (y1 + y2) / 2;
  return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
}

// ─── Grouping logic ─────────────────────────────────────────────────
function buildGroups(studies) {
  const withChain = studies.filter(s => s.causal_chain && s.causal_chain.length > 0);
  const withoutChain = studies.filter(s => !s.causal_chain || s.causal_chain.length === 0);

  const map = new Map();
  for (const study of withChain) {
    const key = normalizeRoot(study.causal_chain[0].description);
    if (!map.has(key)) {
      map.set(key, {
        rootLabel: study.causal_chain[0].description,
        normalizedKey: key,
        studies: [],
      });
    }
    map.get(key).studies.push(study);
  }

  const groups = Array.from(map.values()).sort((a, b) => b.studies.length - a.studies.length);
  // Sort studies within each group by score descending
  for (const g of groups) {
    g.studies.sort((a, b) => (b.score_rh || 0) - (a.score_rh || 0));
  }

  return { groups, withoutChain };
}

// ─── Layout calculation ─────────────────────────────────────────────
function computeLayout(groups) {
  const { NODE_W, NODE_H, ROOT_NODE_H, ROOT_Y, BRANCH_START_Y, STEP_Y, GROUP_GAP, BRANCH_GAP, PADDING } = LAYOUT;

  let cursorX = PADDING;
  let maxChainLen = 0;

  const layoutGroups = groups.map(group => {
    const groupWidth = group.studies.length * NODE_W + (group.studies.length - 1) * BRANCH_GAP;
    const rootX = cursorX + groupWidth / 2 - NODE_W / 2;
    const rootCx = cursorX + groupWidth / 2;

    const branches = group.studies.map((study, idx) => {
      const branchX = cursorX + idx * (NODE_W + BRANCH_GAP);
      const branchCx = branchX + NODE_W / 2;
      const chainLen = study.causal_chain.length;
      if (chainLen > maxChainLen) maxChainLen = chainLen;

      const nodes = study.causal_chain.map((step, stepIdx) => ({
        x: branchX,
        y: BRANCH_START_Y + stepIdx * STEP_Y,
        cx: branchCx,
        step,
        isFirst: stepIdx === 0,
      }));

      return { study, nodes, branchCx };
    });

    const lg = {
      rootLabel: group.rootLabel,
      rootX,
      rootY: ROOT_Y,
      rootCx,
      rootBottom: ROOT_Y + ROOT_NODE_H,
      studyCount: group.studies.length,
      branches,
      startX: cursorX,
      groupWidth,
    };

    cursorX += groupWidth + GROUP_GAP;
    return lg;
  });

  const totalWidth = cursorX - GROUP_GAP + PADDING;
  const totalHeight = BRANCH_START_Y + maxChainLen * STEP_Y + PADDING;

  return { layoutGroups, totalWidth: Math.max(totalWidth, 400), totalHeight: Math.max(totalHeight, 300) };
}

// ─── Composant principal ────────────────────────────────────────────
const RHCausalGraph = ({ onNavigate }) => {
  const [domain, setDomain] = useState('');
  const [level, setLevel] = useState('');
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredStudy, setHoveredStudy] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('oracle_token');
      const params = {};
      if (domain) params.domain = domain;
      if (level) params.level = level;
      const response = await axios.get('/api/rh/studies/causal-graph', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudies(response.data.studies || []);
    } catch (err) {
      setError('Erreur lors du chargement du graphe causal.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [domain, level]);

  const { groups, withoutChain } = useMemo(() => buildGroups(studies), [studies]);
  const { layoutGroups, totalWidth, totalHeight } = useMemo(() => computeLayout(groups), [groups]);

  const handleStudyClick = (studyId) => {
    onNavigate('rh-detail', studyId);
  };

  const { NODE_W, NODE_H, ROOT_NODE_H } = LAYOUT;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Graphe Causal Inter-Etudes</h1>
        <p className="text-gray-600 mt-2">
          Arbre convergent des chaines causales regroupees par noeud racine commun
        </p>
      </div>

      {/* Filtres */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Domaine</label>
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous</option>
            <option value="humain">Humain</option>
            <option value="ecologique">Ecologique</option>
            <option value="culturel">Culturel</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous</option>
            <option value="R1">R1</option>
            <option value="R2">R2</option>
            <option value="R3">R3</option>
            <option value="R4">R4</option>
            <option value="R5">R5</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && groups.length === 0 && (
        <div className="text-center py-12">
          <div className="text-5xl mb-4">🔗</div>
          <p className="text-gray-600 text-lg">Aucune donnee causale disponible.</p>
          <p className="text-gray-400 mt-1">Creez des etudes avec des chaines causales pour alimenter le graphe.</p>
        </div>
      )}

      {/* SVG Tree */}
      {!loading && !error && groups.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-gray-50">
          <svg
            width={totalWidth}
            height={totalHeight}
            viewBox={`0 0 ${totalWidth} ${totalHeight}`}
            className="min-w-full"
          >
            {/* Edges: root -> first step */}
            {layoutGroups.map((lg, gi) =>
              lg.branches.map((branch, bi) => (
                <path
                  key={`edge-r-${gi}-${bi}`}
                  d={bezierPath(
                    lg.rootCx,
                    lg.rootBottom,
                    branch.branchCx,
                    branch.nodes[0]?.y || LAYOUT.BRANCH_START_Y
                  )}
                  stroke={domainColor(branch.study.domain)}
                  strokeWidth={hoveredStudy === branch.study.id ? 3 : 1.5}
                  fill="none"
                  opacity={hoveredStudy && hoveredStudy !== branch.study.id ? 0.2 : 0.6}
                />
              ))
            )}

            {/* Edges: step -> step within branch */}
            {layoutGroups.map((lg, gi) =>
              lg.branches.map((branch, bi) =>
                branch.nodes.slice(0, -1).map((node, ni) => (
                  <line
                    key={`edge-s-${gi}-${bi}-${ni}`}
                    x1={node.cx}
                    y1={node.y + NODE_H}
                    x2={branch.nodes[ni + 1].cx}
                    y2={branch.nodes[ni + 1].y}
                    stroke={domainColor(branch.study.domain)}
                    strokeWidth={hoveredStudy === branch.study.id ? 2.5 : 1.5}
                    opacity={hoveredStudy && hoveredStudy !== branch.study.id ? 0.2 : 0.5}
                  />
                ))
              )
            )}

            {/* Root nodes */}
            {layoutGroups.map((lg, gi) => (
              <g key={`root-${gi}`}>
                <rect
                  x={lg.rootX}
                  y={lg.rootY}
                  width={NODE_W}
                  height={ROOT_NODE_H}
                  rx={10}
                  fill="#1e293b"
                  stroke="#475569"
                  strokeWidth={2}
                />
                <title>{lg.rootLabel}</title>
                <text
                  x={lg.rootCx}
                  y={lg.rootY + 25}
                  textAnchor="middle"
                  fill="#f8fafc"
                  fontSize={13}
                  fontWeight="bold"
                >
                  {truncate(lg.rootLabel, 28)}
                </text>
                <text
                  x={lg.rootCx}
                  y={lg.rootY + 45}
                  textAnchor="middle"
                  fill="#94a3b8"
                  fontSize={11}
                >
                  {lg.studyCount} etude{lg.studyCount > 1 ? 's' : ''}
                </text>
              </g>
            ))}

            {/* Branch nodes */}
            {layoutGroups.map((lg, gi) =>
              lg.branches.map((branch, bi) =>
                branch.nodes.map((node, ni) => {
                  const isHovered = hoveredStudy === branch.study.id;
                  const isDimmed = hoveredStudy && !isHovered;
                  const color = domainColor(branch.study.domain);

                  return (
                    <g
                      key={`node-${gi}-${bi}-${ni}`}
                      onClick={() => handleStudyClick(branch.study.id)}
                      onMouseEnter={() => setHoveredStudy(branch.study.id)}
                      onMouseLeave={() => setHoveredStudy(null)}
                      style={{ cursor: 'pointer' }}
                      opacity={isDimmed ? 0.3 : 1}
                    >
                      <title>{node.step.description}</title>
                      <rect
                        x={node.x}
                        y={node.y}
                        width={NODE_W}
                        height={NODE_H}
                        rx={8}
                        fill={isHovered ? '#f0f9ff' : '#ffffff'}
                        stroke={color}
                        strokeWidth={isHovered ? 2.5 : 1.5}
                      />
                      {/* Left color bar */}
                      <rect
                        x={node.x}
                        y={node.y}
                        width={5}
                        height={NODE_H}
                        rx={2}
                        fill={color}
                      />

                      {node.isFirst ? (
                        <>
                          {/* Sub-domain label */}
                          <text
                            x={node.x + 14}
                            y={node.y + 20}
                            fill="#1e293b"
                            fontSize={13}
                            fontWeight="bold"
                          >
                            {truncate(branch.study.sub_domain || branch.study.title, 22)}
                          </text>
                          {/* Level badge */}
                          <text
                            x={node.x + 14}
                            y={node.y + 38}
                            fill="#64748b"
                            fontSize={10}
                          >
                            {branch.study.level}
                          </text>
                          {/* Score badge */}
                          <rect
                            x={node.x + NODE_W - 62}
                            y={node.y + 14}
                            width={50}
                            height={24}
                            rx={12}
                            fill={scoreBadgeColor(branch.study.score_rh)}
                            opacity={0.15}
                          />
                          <text
                            x={node.x + NODE_W - 37}
                            y={node.y + 31}
                            textAnchor="middle"
                            fill={scoreBadgeColor(branch.study.score_rh)}
                            fontSize={12}
                            fontWeight="bold"
                          >
                            {branch.study.score_rh !== null
                              ? branch.study.score_rh.toFixed(2)
                              : '--'}
                          </text>
                        </>
                      ) : (
                        <>
                          {/* Chain step description */}
                          <text
                            x={node.x + 14}
                            y={node.y + 21}
                            fill="#374151"
                            fontSize={11}
                          >
                            {truncate(node.step.description, 28)}
                          </text>
                          {/* Step number */}
                          <text
                            x={node.x + 14}
                            y={node.y + 38}
                            fill="#9ca3af"
                            fontSize={10}
                          >
                            Maillon {node.step.step}
                          </text>
                          {/* Value if present */}
                          {node.step.value && (
                            <text
                              x={node.x + NODE_W - 10}
                              y={node.y + 31}
                              textAnchor="end"
                              fill="#6b7280"
                              fontSize={10}
                            >
                              {truncate(String(node.step.value), 10)}
                            </text>
                          )}
                        </>
                      )}
                    </g>
                  );
                })
              )
            )}
          </svg>
        </div>
      )}

      {/* Studies without chain */}
      {!loading && withoutChain.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500 mb-2">
            {withoutChain.length} etude{withoutChain.length > 1 ? 's' : ''} sans chaine causale :
          </p>
          <div className="flex flex-wrap gap-2">
            {withoutChain.map(s => (
              <button
                key={s.id}
                onClick={() => handleStudyClick(s.id)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                {s.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RHCausalGraph;
